import React, { useEffect, useState } from "react";
import { useLocation, RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Form, Input, Switch, Radio } from "formik-antd";
import { Page, setPage } from "features/appstate/AppStateSlice";
import {
  listClientSettings,
  addOrUpdateClientSettings,
  getLanguageSettingsListAll,
} from "./ClientSettingsActions";
import {
  ClientSettingsWithDetails,
  ClientSettingsAddForm,
  LanguageSettings,
} from "api/sms-api";
import qs from "qs";
import { Row, Col, Button, Spin } from "antd";
import { RootState } from "store/store";
import i18n from "i18n";
import {
  surveyTypeStore,
  buttonDiscriminator,
  LanguageSettingsCode,
  isEnabledAllowedItems,
  defaultClientSettingsValues,
  defaultEnglishClientSettingsValues,
} from "../../constants";
import SaveButton from "components/SaveButton";
import { RadioChangeEvent } from "antd/lib/radio";

interface ClientSettingsButtonProps {
  items: Array<ClientSettingsWithDetails>;
  loading: boolean;
  listClientSettings: (
    surveyType: string,
    discriminator: string,
    languageSettingsCode: string,
    isBlankLoad?: boolean
  ) => void;
  addOrUpdateClientSettings: (params: Array<ClientSettingsAddForm>) => void;
  languageSettings: Array<LanguageSettings>;
  getLanguageSettingsListLoading: boolean;
  getLanguageSettingsListAll: () => void;
  setPage: (page: Page) => void;
}

const ClientSettingsButton: React.FC<
  ClientSettingsButtonProps & RouteComponentProps
> = (props) => {
  const location = useLocation();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [languageSettingMode, setLanguageSettingMode] = useState<string>(
    LanguageSettingsCode.ja
  );

  useEffect(() => {
    props.setPage(Page.buttonDisplaySettings);
  }, [props.setPage]);

  useEffect(() => {
    props.listClientSettings(
      (parsed.surveyType || surveyTypeStore) as string,
      buttonDiscriminator,
      languageSettingMode
    );
  }, [props.listClientSettings]);

  useEffect(() => {
    props.getLanguageSettingsListAll();
  }, [props.getLanguageSettingsListAll]);

  if (props.loading || props.getLanguageSettingsListLoading) {
    return <Spin />;
  }

  const handleSubmit = async (form: any) => {
    const keys: Array<string> = Object.keys(form).filter((key: string) =>
      props.items.find((item: ClientSettingsWithDetails) => item.code === key)
    );
    const forms: Array<ClientSettingsAddForm> = keys.map((key: string) => {
      const isEnabledAllowedItem = isEnabledAllowedItems[key];
      const item = props.items.find(
        (item: ClientSettingsWithDetails) => item.code === key
      );
      const languageSetting = props.languageSettings.find(
        (ls) => ls.code === form.languageSetting
      );
      if (item) {
        return {
          settingsTypeId: item.settingsTypeId,
          languageSettingsId: languageSetting ? languageSetting.id : "",
          settingsValue: form[key],
          isEnabled: isEnabledAllowedItem
            ? form[isEnabledAllowedItem]
            : undefined,
          surveyType: (parsed.surveyType || surveyTypeStore) as string,
        };
      } else {
        return {
          settingsTypeId: "",
          languageSettingsId: languageSetting ? languageSetting.id : "",
          settingsValue: "",
          surveyType: (parsed.surveyType || surveyTypeStore) as string,
        };
      }
    });
    await props.addOrUpdateClientSettings(forms);
    await props.listClientSettings(
      (parsed.surveyType || surveyTypeStore) as string,
      buttonDiscriminator,
      form.languageSetting
    );
  };

  const handleReset = async () => {
    await props.listClientSettings(
      (parsed.surveyType || surveyTypeStore) as string,
      buttonDiscriminator,
      languageSettingMode,
      true
    );
  };

  const handleLanguageSettingChange = async (e: RadioChangeEvent) => {
    if (e.target.value) {
      await props.listClientSettings(
        (parsed.surveyType || surveyTypeStore) as string,
        buttonDiscriminator,
        e.target.value as string
      );
      setLanguageSettingMode(e.target.value as string);
    }
  };

  const initialValues: any = {
    languageSetting: languageSettingMode,
  };

  const formItems = props.items.map((item: ClientSettingsWithDetails) => {
    const isEnabledAllowedItem = isEnabledAllowedItems[item.code];
    const defaultSettingsValues =
      languageSettingMode === LanguageSettingsCode.ja
        ? defaultClientSettingsValues
        : defaultEnglishClientSettingsValues;
    const defaultValue = defaultSettingsValues[item.code] || "";
    initialValues[item.code] =
      item?.clientSettings?.settingsValue ?? defaultValue;
    // Set the language
    const languageSettingsId = item?.clientSettings?.languageSettingsId ?? "";
    const languageSetting = props.languageSettings.find(
      (ls) => ls.id === languageSettingsId
    );
    initialValues.languageSetting = languageSetting
      ? languageSetting.code
      : languageSettingMode;
    let result = (
      <Row key={item.code} justify="start">
        <Col span={18}>
          <Form.Item
            name={item.code}
            label={item.name}
            validateTrigger={["onBlur"]}
            rules={[
              {
                validator(_, value) {
                  if (value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(i18n.t("validation.required"))
                  );
                },
              },
            ]}
          >
            <Input name={item.code} />
          </Form.Item>
        </Col>
      </Row>
    );
    if (isEnabledAllowedItem) {
      initialValues[isEnabledAllowedItem] =
        item?.clientSettings?.isEnabled ?? true;
      result = (
        <Row key={item.code} justify="start">
          <Col span={10}>
            <Form.Item
              name={item.code}
              label={item.name}
              validateTrigger={["onBlur"]}
              rules={[
                {
                  validator(_, value) {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(i18n.t("validation.required"))
                    );
                  },
                },
              ]}
            >
              <Input name={item.code} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              className="client-settings-is-enabled-button"
              name={isEnabledAllowedItem}
              label={i18n.t("clientSettings.isEnabled")}
            >
              <Switch name={isEnabledAllowedItem} />
            </Form.Item>
          </Col>
        </Row>
      );
    }
    return result;
  });

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {() => {
          return (
            <Form layout="vertical">
              <Row justify="start">
                <Col span={18}>
                  <Form.Item name="languageSetting">
                    <Radio.Group
                      name="languageSetting"
                      className="form-radio-group"
                      defaultValue={initialValues.languageSetting}
                      onChange={handleLanguageSettingChange}
                    >
                      <Radio.Button
                        name={LanguageSettingsCode.ja}
                        value={LanguageSettingsCode.ja}
                      >
                        {i18n.t("clientSettings.japanese")}
                      </Radio.Button>
                      <Radio.Button
                        name={LanguageSettingsCode.en}
                        value={LanguageSettingsCode.en}
                      >
                        {i18n.t("clientSettings.english")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="start">
                <Col span={18}>
                  <Button
                    type="primary"
                    className="mail-exclusion-padding"
                    onClick={handleReset}
                  >
                    {i18n.t("clientSettings.resetToDefaults")}
                  </Button>
                </Col>
              </Row>
              {formItems}
              <Row justify="end" style={{ marginTop: "2.5em" }}>
                <SaveButton />
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    items: state.clientSettings.items,
    loading: state.clientSettings.listLoading,
    languageSettings: state.clientSettings.languageSettings,
    getLanguageSettingsListLoading:
      state.clientSettings.getLanguageSettingsListLoading,
  };
};

const mapDispatchToProps = {
  setPage,
  listClientSettings,
  addOrUpdateClientSettings,
  getLanguageSettingsListAll,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClientSettingsButton);
